import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { Auth, Storage, API } from 'aws-amplify';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    currentUser: null,
    currentAuthState: null,
    currentChallengeState: null,
    currentCognitoException: null,
    currentCognitoExceptionMessage: null,
    currentFundingApplication: null,
    allFundingApplications: [],
    currentUserUploads: [],
  },
  getters: {
    getFundingApplicationById: (state) => (
      (id) => state.allFundingApplications.find((application) => application.applicationId === id)),
    getCurrentApplication: (state) => state.currentFundingApplication,
    getCurrentUserUploads: (state) => state.currentUserUploads,
  },
  mutations: {
    setUser(state, payload) {
      state.currentUser = payload;
    },
    setAuthState(state, payload) {
      state.currentAuthState = payload;
    },
    setChallengeState(state, payload) {
      state.currentChallengeState = payload;
    },
    setCognitoExceptionState(state, payload) {
      state.currentCognitoException = payload;
    },
    setCognitoExceptionMessage(state, payload) {
      state.currentCognitoExceptionMessage = payload;
    },
    setFundingApplication(state, payload) {
      state.currentFundingApplication = payload;
    },
    addFundingApplication(state, payload) {
      state.allFundingApplications.push(payload);
    },
    saveFundingApplication(state, payload) {
      // TODO: update db?
      state.currentFundingApplication = payload;
    },
    retrieveAllApplications(state, payload) {
      state.allFundingApplications = payload;
    },
    addFile(state, payload) {
      state.currentUserUploads.push(payload);
    },
    retrieveAllUploads(state, payload) {
      state.currentUserUploads = payload;
    },
  },
  actions: {
    async signOut({ dispatch }) {
      try {
        await Auth.signOut();
        // Clear auth states
        dispatch('resetAuthStates');
      } catch (error) {
        // TODO: do something with error
        if (error) {
          // Do nothing
        }
      }
    },
    setUser(context, payload) {
      context.commit('setUser', payload);
    },
    setAuthState(context, payload) {
      context.commit('setAuthState', payload);
    },
    setChallengeState(context, payload) {
      context.commit('setChallengeState', payload);
    },
    setCognitoException(context, payload) {
      context.commit('setCognitoExceptionState', payload.code || null);
      context.commit('setCognitoExceptionMessage', payload.message || null);
    },
    resetAuthStates(context) {
      // Reset user
      context.commit('setUser', null);
      // Reset auth
      context.commit('setAuthState', null);
      // Reset challenge
      context.commit('setChallengeState', null);
      // Reset exception
      context.commit('setCognitoExceptionState', null);
      context.commit('setCognitoExceptionMessage', null);
    },
    submitContactUs(context, payload) {
      const updateObj = omitBy(payload, isNil());
      const handleUrl = `${process.env.VUE_APP_FUND_HANDLE_URL}/${process.env.VUE_APP_STAGE}/contact`;
      return axios({
        method: 'post',
        url: handleUrl,
        data: updateObj,
      });
    },
    submitInvestors(context, payload) {
      const updateObj = omitBy(payload, isNil());
      const handleUrl = `${process.env.VUE_APP_FUND_HANDLE_URL}/${process.env.VUE_APP_STAGE}/investors`;
      return axios({
        method: 'post',
        url: handleUrl,
        data: updateObj,
      });
    },
    submitApplication(context, payload) {
      // Remove unnecessary files and save new version
      const updateObj = omitBy(payload, (value, key) => isNil(value) || key === 'request' || key === 'applicationId' || key === 'userId');
      return API.post('apply', '/apply', {
        body: updateObj,
      });
    },
    uploadFile(context, payload) {
      // Upload to S3, successful result updates store
      Storage.vault.put(`documents/${payload.name}`, payload, {
        contentType: payload.type,
      })
        .then(() => {
          // Trigger refresh of files in state
          this.dispatch('getAllFiles');
        })
        .catch((error) => {
          if (error) {
            // Do nothing
          }
        });
    },
    deleteFile(context, payload) {
      // Remove file by id
      Storage.vault.remove(payload)
        .then((result) => {
          if (result) {
            // Do nothing
          }
        })
        .catch((error) => {
          if (error) {
            // Do nothing
          }
        });
    },
    getAllFiles(context) {
      // Get all files
      return new Promise((resolve, reject) => {
        Storage.vault.list('documents/')
          .then((result) => {
            // Add to store
            context.commit('retrieveAllUploads', result);
            // Resolve
            resolve();
          })
          .catch((error) => {
            if (error) {
              // Do nothing
            }
            // Reject
            reject();
          });
      });
    },
    getAllApplicationsByUser(context) {
      // Get all applications
      return new Promise((resolve, reject) => {
        API.get('getApps', '/apply/get')
          .then((results) => {
            // Sort applications by date
            results.sort((a, b) => b.request.requestTimeEpoch - a.request.requestTimeEpoch);
            // Add to store
            context.commit('retrieveAllApplications', results);
            /**
             * Set current application to first item in results
             * TODO: Handle selection based on some criteria
             */
            context.commit('setFundingApplication', results[0]);
            // Resolve
            resolve();
          })
          .catch((error) => {
            if (error) {
              // Do nothing
            }
            // Reject
            reject();
          });
      });
    },
  },
  modules: {
  },
});
